import type { RequestConfig } from '@chikara/types';

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => string | null;
  /**
   * Allows skipping Authorization header for public endpoints (e.g. login).
   * If omitted, Authorization is attached whenever getToken() returns a token.
   */
  shouldAttachAuth?: (endpoint: string, options: ApiRequestOptions) => boolean;
  onUnauthorized?: () => void;
  onError?: (error: unknown) => void;
  onNetworkError?: (error: unknown) => void;
  defaultHeaders?: Record<string, string>;
}

export interface ApiRequestOptions extends RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
}

export class ApiClient {
  private config: ApiClientConfig;

  constructor(config: ApiClientConfig) {
    this.config = config;
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401 && this.config.onUnauthorized) {
        this.config.onUnauthorized();
      }

      if (this.config.onError) {
        await this.config.onError(response);
      }

      throw new Error('Unhandled API error');
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return undefined as T;
    }

    let data;
    try {
      const clone = response.clone();
      data = await clone.json();
    } catch {
      // Not JSON, continue normally with original error handling
      return response.json();
    }
    if (data && data.data && typeof data.data === 'object' && 'response' in data.data && data.data.response === 'BAD') {
      if (this.config.onError) {
        const fakeResponse = new Response(JSON.stringify(data.data), {
          status: 400,
          statusText: 'Bad Request',
          headers: response.headers
        });
        await this.config.onError(fakeResponse);
      }
      throw new Error('Unhandled API error (BAD response)');
    }

    return data as T;
  }

  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean> | string): string {
    const url = new URL(`${this.config.baseUrl}${endpoint}`);

    if (params) {
      if (typeof params === "object") {
        Object.entries(params).forEach(([key, value]) => {
          url.searchParams.append(key, String(value));
        });
      } else if (typeof params === "string") {
        url.pathname += `/${params}`;
      }
    }

    return url.toString();
  }

  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const { method = 'GET', body, headers, params, timeout, signal } = options;

    const shouldAttachAuth = this.config.shouldAttachAuth
      ? this.config.shouldAttachAuth(endpoint, options)
      : true;

    const token = shouldAttachAuth ? this.config.getToken?.() : null;

    const requestHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...this.config.defaultHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    };

    const controller = new AbortController();
    const timeoutId = timeout ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      const response = await fetch(this.buildUrl(endpoint, params), {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
        signal: signal || controller.signal,
      });

      return this.handleResponse<T>(response);
    } catch (error) {
      if (this.config.onNetworkError) {
        this.config.onNetworkError(error);
      }
      throw error;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  }

  private wrapPayload(body: unknown): unknown {
    if (body === undefined || body === null) {
      return { data: {} };
    }

    if (typeof body === 'object' && !Array.isArray(body)) {
      const keys = Object.keys(body as Record<string, unknown>);
      if (keys.length === 1 && keys[0] === 'data') {
        return body;
      }
    }

    return { data: body };
  }

  // Convenience methods
  async get<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'POST', body: this.wrapPayload(body) });
  }

  async put<T>(endpoint: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PUT', body: this.wrapPayload(body) });
  }

  async patch<T>(endpoint: string, body?: unknown, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'PATCH', body: this.wrapPayload(body) });
  }

  async delete<T>(endpoint: string, options?: Omit<ApiRequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Factory function for creating configured API clients
export function createApiClient(config: ApiClientConfig): ApiClient {
  return new ApiClient(config);
}
